'use client';

import { fetchReserveInfo } from '@/apis/happy/index.api';
import JDILogin from '@/app/login/utils';
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIProductCardV2 from '@/components/productCard/components/type1';
import SafeLink from '@/components/SafeLink';
import UKDrawer from '@/components/UKDrawer';
import { useTranslation } from '@/i18n/client';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/23622';
import { useRouter } from 'next/navigation';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import styles from './style.module.scss';

// ---------------- icon ------------------------
import ArrowRight from '@/assets/icons/ArrowRight.svg';
import MyReservation from './component/MyReservation';
import Back from './icon/back.svg';
import Calendar from './icon/calendar.svg';
import Fire from './icon/fire.svg';
import RulesInfo from './icon/rulesInfo.svg';

export default function Module({
  moduleData,
  currentMills,
  localInfo,
}: {
  moduleData: any;
  currentMills: number;
  localInfo: any;
}) {
  const router = useRouter();
  const { externalData, dataBatch } = moduleData;
  const titleInfo = dataBatch.titleInfo;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [drawerTitle, setDrawerTitle] = useState(`${t('flashSales.reservation')}`);
  const [reservationData, setReservationData] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false); // 如果登录，调用预约信息接口，如果未登录，无预约信息。
  const [dataReady, setDataReady] = useState(false); // 预约信息是否加载完成
  const [reserveNum, setReserveNum] = useState(0); // 如果登录，展示预约总数
  const iconPath = `${t('flashSales.iconPath')}`;
  const Flash = useMemo(() => lazy(() => import(`${iconPath}`)), [iconPath]);

  const itemData = () => {
    const data = externalData?.data;
    if (data && data.length > 0) {
      return data;
    }
    return [];
  };

  // ----------------------- 生成埋点自定义参数 -------------------------------

  const genJsonParam = (product: any, index: number) => {
    const json_param: any = {
      skuid: isNaN(product?.skuId) ? '-100' : product.skuId.toString(),
      firprice: isNaN(product?.realPrice) ? -100 : Number(product?.realPrice),
      firpricetype: '11',
      index: index,
    };
    return json_param;
  };
  const genEventTrackingInfo = (product: any, index: number) => {
    const eventTrackingInfo = {} as { [key: string]: any };
    const json_param = genJsonParam(product, index);
    const expConfig = {
      useClick: false,
      stay_duration: 0.3,
      repeated: false,
      area_rate: 0.5,
    };
    eventTrackingInfo['productClickPoint'] = () =>
      sendClickTrack.Activity_FlashSales_HappyHour_Sku(json_param);
    eventTrackingInfo['productExpConfig'] = {
      event_id: EXPOSURE_RECORD.Activity_FlashSales_HappyHour_Sku_Expo,
      config: expConfig,
      json_param: json_param,
    };

    return eventTrackingInfo;
  };

  // -------------- Product Data -------------------- //
  const getProductData = (product: any, index: number) => {
    return {
      type: '1',
      batchId: product?.batchId,
      groupId: product?.groupId,
      skuId: product?.skuId,
      skuName: product?.skuName,
      skuImg: product?.skuImg,
      realPrice: product?.realPrice,
      currency: localInfo?.currencyCode,
      languageCode: localInfo?.languageCode,
      shouldShowCard: false,
      shouldShowSkuName: false,
      trackingPointParams: genEventTrackingInfo(product, index),

      // -------------- styles-------------------- //
      containerStyle: {
        flex: 1,
        width: '100%',
        height: '100%',
        minWidth: '88px',
        minHeight: '116px',
      },
      skuImgStyle: {
        width: '88px',
        height: '88px',
      },
      priceStyle: {
        fontSize: '12px',
        marginRight: '0px',
        justifyContent: 'center',
        display: 'flex',
      },
      realPriceJDIPriceParams: {
        symbolStyle: { fontSize: '12px' },
        integerStyle: { fontSize: '18px' },
        decimalPointStyle: { fontSize: '12px' },
        decimalPlaceStyle: { fontSize: '12px' },
        uiType: 1,
        containerStyle: {
          marginTop: '8px',
          lineHeight: '18px',
          height: '100%',
        },
      },
    };
  };

  const isItemEmpty = () => {
    return itemData() && itemData().length > 3;
  };

  const gotoHappyHour = (item: any) => {
    // /happy?batchId=xxx batchId 可选
    console.log('gotoHappyHour11', item);
    if (!item) {
      return;
    }
    router.push(`/happy?batchId=${item.batchId}&groupId=${item.groupId}`);
  };

  const gotoRules = () => {
    router.push('/help/flash-sales-rules?hideTitleBar=1');
  };
  // ------------------------ 预约信息处理 -------------------------- //

  // 先判断用户是否登录
  useEffect(() => {
    const ifLoggedIn = async () => {
      const { isLoggedIn } = await JDILogin.getInstance().getUserInfo();
      setIsLoggedIn(isLoggedIn);
    };
    ifLoggedIn();
  }, []);

  const genReserveData = () => {
    fetchReserveInfo(5).then((res) => {
      if (res?.code === '200') {
        if (res.data && res.data.length > 0) {
          setReservationData(res.data);
        }
      } else {
        // UKToast.show({
        //   content: res.msg, // 弹窗内容
        //   icon: "error", // 可选：success, fail, loading, warn, info，影响图标
        // });
        console.log('fetch error ', res.msg);
      }
    });
  };
  // 如果用户已登录，且无预约信息，就调用接口获取用户预约信息
  if (isLoggedIn && !dataReady) {
    genReserveData();
    setDataReady(true);
  }

  // 如果预约信息发生变化，计算预约总数并展示。未登录不展示预约总数。
  useEffect(() => {
    let sum = 0;
    reservationData.forEach((item: any) => {
      sum += item?.skus?.length;
    });
    setReserveNum(sum);
  }, [reservationData]);

  useEffect(() => {
    if (reserveNum > 1) {
      setDrawerTitle(`${t('flashSales.reservation')} (${reserveNum})`);
    } else if (reserveNum === 1) {
      setDrawerTitle(`${t('flashSales.reservation')} (${reserveNum})`);
    } else {
      setDrawerTitle(`${t('flashSales.reservation')}`);
    }
  }, [reserveNum]);

  // 点击日历，如果未登录，跳转登录页；如果已登录，打开预约弹窗
  const handleClickCalendar = () => {
    if (isLoggedIn) {
      setIsOpen(true);
    } else {
      JDILogin.getInstance().goToLoginPage({
        returnUrl: window.location.pathname + window.location.search,
      });
    }
  };

  return (
    <div className={styles['uk-h5-seckill-happy-hour']}>
      {isItemEmpty() ? (
        <div style={{ height: '260px', width: '100%', overflow: 'hidden' }}>
          <CDNImage
            src={titleInfo[0]?.bgLargeImage?.imageUrl || ''}
            width={2500}
            height={240}
            alt='seckill_happy_img'
            // style={{ objectFit: "contain" }}
          />
        </div>
      ) : (
        <a style={{ height: '76px', width: '100%', backgroundColor: 'red' }}></a>
      )}
      <div className={styles.bgContainer}>
        <div className={styles.topInfo}>
          {/* <div> */}
          <div className={styles.flexContainer}>
            <Back
              onClick={() => {
                router.back();
              }}
            />
            <Suspense>
              <Flash />
            </Suspense>
            <div className={styles.buttonGroup}>
              <div>
                {isLoggedIn && reserveNum > 0 && <div className={styles.cartNum}>{reserveNum}</div>}
                <Calendar onClick={handleClickCalendar} />
              </div>
              <div
                onClick={() => {
                  sendClickTrack.Activity_FlashSales_Rule();
                  gotoRules();
                }}
              >
                <RulesInfo />
              </div>
            </div>
          </div>
          <span className={styles.tagInfo}>
            <Fire />
            <span className={styles.itemText}>{titleInfo[0]?.tagText1}</span>
            <span style={{ marginLeft: '24px' }} className={styles.itemText}>
              <Fire />
              <span style={{ marginLeft: '6px' }}>{titleInfo[0]?.tagText2}</span>
            </span>
          </span>
          {/* </div> */}
        </div>
        {isItemEmpty() && (
          <div className={styles.container}>
            <div className={styles.header}>
              <span>
                <span className={styles.headerTitle}>{titleInfo[0]?.mainTitle}</span>
                <span className={styles.subTitle}>{titleInfo[0]?.subTitle}</span>
              </span>
              <div
                className={styles.buttonContainer}
                onClick={() => sendClickTrack.Activity_FlashSales_HappyHour_Viewmore()}
              >
                <SafeLink href='/happy'>
                  <div className={styles.headerButton}>
                    <p>{t('flashSales.viewMore')}</p>
                    <ArrowRight className={styles.headerIcon} />
                  </div>
                </SafeLink>
              </div>
            </div>
            <div className={styles.cardContainer}>
              {itemData() &&
                itemData().length > 0 &&
                itemData().map((item: any, index: number) => {
                  return (
                    <JDIProductCardV2
                      key={item.skuId}
                      // @ts-ignore
                      productData={{
                        ...getProductData(item, index),
                        onClickProduct: gotoHappyHour,
                      }}
                    />
                  );
                })}
            </div>
          </div>
        )}
      </div>
      <UKDrawer
        openDrawer={isOpen}
        includeBottomBar={true}
        bottomBarHeight={48}
        setOpenDrawer={() => setIsOpen(false)}
        title={drawerTitle}
        height={500}
      >
        <div className={styles.listContainer}>
          <MyReservation
            data={reservationData}
            currentMills={currentMills}
            localInfo={localInfo}
          ></MyReservation>
        </div>
      </UKDrawer>
    </div>
  );
}
